import { useEffect, useState } from "react";
import { Download, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fdate, downloadPdf } from "@/lib/api";
import { PageHeader, StatusBadge, TableWrap, Th, Td, Empty, Field, NSelect, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const PaymentDialog = ({ inv, onClose, onSaved }) => {
  const remaining = inv ? +(inv.total - inv.amount_paid).toFixed(2) : 0;
  const [p, setP] = useState({ amount: "", method: "Virement", reference: "", date: new Date().toISOString().slice(0, 10) });
  useEffect(() => { if (inv) setP((x) => ({ ...x, amount: remaining })); }, [inv]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try { await api.post(`/invoices/${inv.id}/payments`, { ...p, amount: Number(p.amount) }); toast.success("Paiement enregistré"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={!!inv} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-dialog">
        <DialogHeader><DialogTitle>Paiement — {inv?.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Reste à payer : <b>{money(remaining)}</b></p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Montant (USD)"><Input type="number" step="any" value={p.amount} onChange={(e) => setP({ ...p, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
          <Field label="Date"><Input type="date" value={p.date} onChange={(e) => setP({ ...p, date: e.target.value })} data-testid="payment-date-input" /></Field>
          <Field label="Mode">
            <NSelect value={p.method} onChange={(v) => setP({ ...p, method: v })} testId="payment-method-select">
              {["Virement", "Espèces", "Chèque", "Mobile money"].map((m) => <option key={m}>{m}</option>)}
            </NSelect>
          </Field>
          <Field label="Référence"><Input value={p.reference} onChange={(e) => setP({ ...p, reference: e.target.value })} data-testid="payment-reference-input" /></Field>
        </div>
        {inv?.payments?.length > 0 && (
          <ul className="space-y-1 border-t pt-3 text-xs text-slate-600">
            {inv.payments.map((x, i) => <li key={i}>{fdate(x.date)} · {x.method} · {money(x.amount)} {x.reference && `(${x.reference})`}</li>)}
          </ul>
        )}
        <DialogFooter><Button className="bg-red-600 hover:bg-red-700" onClick={save} data-testid="payment-save-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(null);
  const load = () => api.get("/invoices").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const total = items.reduce((a, i) => a + i.total, 0);
  const paid = items.reduce((a, i) => a + i.amount_paid, 0);

  return (
    <div>
      <PageHeader kicker="Gestion commerciale" title="Factures" />
      <div className="mb-6 grid gap-4 sm:grid-cols-3">
        <StatCard label="Total facturé" value={money(total)} testId="invoices-total" />
        <StatCard label="Encaissé" value={money(paid)} accent="#059669" testId="invoices-paid" />
        <StatCard label="Reste à encaisser" value={money(total - paid)} accent="#DC2626" testId="invoices-outstanding" />
      </div>
      {items.length ? (
        <TableWrap>
          <thead><tr><Th>N°</Th><Th>Client</Th><Th>Commande</Th><Th>Échéance</Th><Th className="text-right">Total</Th><Th className="text-right">Payé</Th><Th>Statut</Th><Th className="text-right">Actions</Th></tr></thead>
          <tbody>
            {items.map((i) => {
              const late = i.status !== "paye" && i.due_date < new Date().toISOString().slice(0, 10);
              return (
                <tr key={i.id} data-testid={`invoice-row-${i.number}`} className="hover:bg-slate-50">
                  <Td className="font-mono text-xs">{i.number}</Td>
                  <Td className="font-medium">{i.client_name}</Td>
                  <Td className="font-mono text-xs text-slate-500">{i.order_number}</Td>
                  <Td className={late ? "font-semibold text-red-600" : ""}>{fdate(i.due_date)}{late && " · en retard"}</Td>
                  <Td className="text-right font-semibold">{money(i.total)}</Td>
                  <Td className="text-right">{money(i.amount_paid)}</Td>
                  <Td><StatusBadge status={i.status} testId={`invoice-status-${i.number}`} /></Td>
                  <Td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" data-testid={`invoice-pdf-${i.number}`} onClick={() => downloadPdf(`/invoices/${i.id}/pdf`, `${i.number}.pdf`)}><Download className="h-3.5 w-3.5" /></Button>
                      {i.status !== "paye" && <Button size="sm" className="bg-blue-900 hover:bg-blue-950" data-testid={`invoice-pay-${i.number}`} onClick={() => setCurrent(i)}><CreditCard className="mr-1 h-3.5 w-3.5" /> Paiement</Button>}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      ) : <Empty testId="invoices-empty" text="Aucune facture. Facturez une commande depuis l'onglet Commandes." />}
      <PaymentDialog inv={current} onClose={() => setCurrent(null)} onSaved={() => { setCurrent(null); load(); }} />
    </div>
  );
}
